import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Typography, Spin, InputNumber, Alert, Progress, theme } from 'antd';
import { IconArrowLeft, IconShieldCheck, IconPlus, IconArrowDownLeft, IconArrowUpRight } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { message } from '../../services/toastMessage';
import AppLayout from '../../components/Layouts/Layout';
import PageColumn from '../../components/Layouts/PageColumn';
import ConfirmBottomSheet from '../../components/ConfirmBottomSheet';
import { BookingPaymentStep } from '../../components/HeavyDutyMachineryBookingWidget';
import { getMyHostDeposit, topUpHostDeposit, closeHostDeposit } from '../../services/HostDepositService';
import { IHostDeposit, maxBookingValueFor } from '../../interfaces/heavyDutyMachinery/IHostDeposit';
import { formatCurrency } from '../../helpers/currencyHelper';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { TEAL, TEAL_SOFT_BG, CORAL, CORAL_SOFT_BG } from '../../theme/creamyTokens';

const { Title, Text } = Typography;

/** Sensible starting figures, so nobody has to invent a number from nothing. */
const SUGGESTED_AMOUNTS = [100, 200, 500];

/**
 * A host's own deposit: what it is, what it buys them, and how to add to it.
 *
 * The screen is built around ONE number -- the biggest job they can still
 * take. A balance of 87,40 EUR tells a host nothing about their own business;
 * "you can take jobs of up to 5.783 EUR" is the same fact in the units they
 * actually work in, and it is the number the booking gate enforces, so it can
 * never turn out to have been optimistic.
 *
 * The calculator does the same thing before any money changes hands: type an
 * amount, see what it unlocks. That is the whole argument for paying in, and
 * it works better than any paragraph explaining processing fees.
 */
const HostDepositPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const [deposit, setDeposit] = useState<IHostDeposit | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState<number | null>(SUGGESTED_AMOUNTS[0]);
  const [clientSecret, setClientSecret] = useState<string | undefined>(undefined);
  const [starting, setStarting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const load = useCallback(async () => {
    try {
      setDeposit(await getMyHostDeposit());
    } catch {
      message.error(translate(t, TRANSLATIONS.HOST_DEPOSIT_LOAD_ERROR));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const money = useCallback(
    (value: number) => formatCurrency(value, deposit?.currencyCode ?? 'EUR', i18n.language),
    [deposit?.currencyCode, i18n.language]
  );

  // Worked out here rather than asked of the server per keystroke -- the two
  // use the same formula, and the server re-checks before anything is
  // accepted, so the live figure can never promise more than the gate allows.
  const calculatorMax = useMemo(() => {
    if (!deposit || !amount) return 0;
    return maxBookingValueFor(amount, deposit.processingFeePercent, deposit.processingFeeFixed);
  }, [deposit, amount]);

  const handleStartTopUp = async () => {
    if (!amount || amount <= 0) return;
    setStarting(true);
    try {
      const topUp = await topUpHostDeposit(amount);
      setClientSecret(topUp.clientSecret);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HOST_DEPOSIT_TOP_UP_ERROR));
    } finally {
      setStarting(false);
    }
  };

  // The balance moves on Stripe's webhook, not on this callback, so the
  // reload below can legitimately come back with the old figure for a second
  // or two. Said out loud in the toast rather than papered over with a
  // spinner that would be lying about what it is waiting for.
  const handlePaid = async () => {
    setClientSecret(undefined);
    message.success(translate(t, TRANSLATIONS.HOST_DEPOSIT_TOP_UP_DONE));
    await load();
  };

  const handleClose = async () => {
    setClosing(true);
    try {
      setDeposit(await closeHostDeposit());
      message.success(translate(t, TRANSLATIONS.HOST_DEPOSIT_CLOSE_DONE));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HOST_DEPOSIT_LOAD_ERROR));
    } finally {
      setClosing(false);
      setConfirmClose(false);
    }
  };

  const renderHeadline = () => {
    if (!deposit) return null;
    const warm = deposit.isExhausted || deposit.isLow;
    return (
      <div
        style={{
          background: warm ? CORAL_SOFT_BG : TEAL_SOFT_BG,
          border: `1px solid ${warm ? CORAL : TEAL}`,
          borderRadius: 18,
          padding: 18,
        }}
      >
        <Text style={{ fontSize: 13, color: warm ? CORAL : TEAL, fontWeight: 600 }}>
          {translate(t, TRANSLATIONS.HOST_DEPOSIT_MAX_BOOKING_LABEL)}
        </Text>
        <Title level={2} style={{ margin: '4px 0 0', color: warm ? CORAL : TEAL }}>
          {money(deposit.maxBookingValue)}
        </Title>

        {/* How much of the balance is already spoken for. A bar rather than
            two numbers, because "87 of 200" is a ratio and the eye reads a
            ratio faster than it does arithmetic. */}
        {deposit.balance > 0 && (
          <Progress
            percent={Math.min(100, Math.round((deposit.currentExposure / deposit.balance) * 100))}
            showInfo={false}
            strokeColor={warm ? CORAL : TEAL}
            style={{ marginTop: 12, marginBottom: 4 }}
          />
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {`${translate(t, TRANSLATIONS.HOST_DEPOSIT_BALANCE_LABEL)}: ${money(deposit.balance)}`}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {`${translate(t, TRANSLATIONS.HOST_DEPOSIT_EXPOSURE_LABEL)}: ${money(deposit.currentExposure)}`}
          </Text>
        </div>
      </div>
    );
  };

  const renderCalculator = () => (
    <div style={{ marginTop: 20 }}>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.HOST_DEPOSIT_CALCULATOR_TITLE)}
      </Text>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        {SUGGESTED_AMOUNTS.map((suggested) => (
          <Button
            key={suggested}
            size="small"
            onClick={() => setAmount(suggested)}
            style={{
              borderRadius: 999,
              borderColor: amount === suggested ? TEAL : token.colorBorder,
              color: amount === suggested ? TEAL : undefined,
            }}
          >
            {money(suggested)}
          </Button>
        ))}
      </div>
      <InputNumber
        value={amount}
        onChange={setAmount}
        min={0}
        step={50}
        precision={2}
        addonAfter={deposit?.currencyCode ?? 'EUR'}
        style={{ width: '100%', borderRadius: 14 }}
      />
      {!!amount && amount > 0 && (
        <div
          style={{
            marginTop: 12,
            padding: '12px 14px',
            borderRadius: 14,
            background: TEAL_SOFT_BG,
            border: `1px solid ${TEAL}`,
          }}
        >
          <Text style={{ fontSize: 14, color: TEAL }}>
            {t(TRANSLATIONS.HOST_DEPOSIT_CALCULATOR_RESULT, {
              deposit: money(amount),
              max: money(calculatorMax),
            })}
          </Text>
        </div>
      )}
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
        {translate(t, TRANSLATIONS.HOST_DEPOSIT_CALCULATOR_HINT)}
      </Text>
      <Button
        type="primary"
        block
        icon={<IconPlus size={16} />}
        loading={starting}
        disabled={!amount || amount <= 0}
        onClick={handleStartTopUp}
        style={{ borderRadius: 999, marginTop: 14, background: TEAL, borderColor: TEAL }}
      >
        {translate(t, TRANSLATIONS.HOST_DEPOSIT_TOP_UP_BUTTON)}
      </Button>
    </div>
  );

  const renderMovements = () => (
    <div style={{ marginTop: 24 }}>
      <Text strong style={{ display: 'block', marginBottom: 10 }}>
        {translate(t, TRANSLATIONS.HOST_DEPOSIT_MOVEMENTS_TITLE)}
      </Text>
      {!deposit?.movements.length ? (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {translate(t, TRANSLATIONS.HOST_DEPOSIT_MOVEMENTS_EMPTY)}
        </Text>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {deposit.movements.map((movement) => {
            const incoming = movement.amount >= 0;
            return (
              <div
                key={movement.id}
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  padding: '10px 12px',
                  borderRadius: 14,
                  border: `1px solid ${token.colorBorderSecondary}`,
                }}
              >
                <span style={{ display: 'flex', flexShrink: 0, color: incoming ? TEAL : CORAL, marginTop: 2 }}>
                  {incoming ? <IconArrowDownLeft size={16} /> : <IconArrowUpRight size={16} />}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* The reason, always. A ledger whose rows say only an
                      amount is a ledger that answers nothing. */}
                  <Text style={{ fontSize: 13, display: 'block' }}>{movement.reason || movement.kind}</Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {new Date(movement.createdAt).toLocaleString(i18n.language)}
                  </Text>
                </div>
                <Text strong style={{ color: incoming ? TEAL : CORAL, whiteSpace: 'nowrap' }}>
                  {`${incoming ? '+' : ''}${money(movement.amount)}`}
                </Text>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <AppLayout>
      <PageColumn maxWidth={640} style={{ padding: '16px 16px 40px' }}>
        <Button type="text" icon={<IconArrowLeft size={18} />} onClick={() => navigate(-1)} style={{ marginBottom: 8 }} />

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 4 }}>
          <IconShieldCheck size={22} color={TEAL} />
          <Title level={3} style={{ margin: 0 }}>
            {translate(t, TRANSLATIONS.HOST_DEPOSIT_TITLE)}
          </Title>
        </div>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {translate(t, TRANSLATIONS.HOST_DEPOSIT_SUBTITLE)}
        </Text>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : !deposit ? null : (
          <div style={{ marginTop: 18 }}>
            {/* Shown even when the platform is not asking for a deposit: a
                host who came here deserves to know the answer is "nothing to
                do", not an empty account that looks like something is
                missing. */}
            {!deposit.required && (
              <Alert
                type="info"
                showIcon
                message={translate(t, TRANSLATIONS.HOST_DEPOSIT_NOT_REQUIRED)}
                style={{ borderRadius: 14, marginBottom: 16 }}
              />
            )}

            {deposit.isExhausted && deposit.required && (
              <Alert
                type="error"
                showIcon
                message={translate(t, TRANSLATIONS.HOST_DEPOSIT_EXHAUSTED_WARNING)}
                style={{ borderRadius: 14, marginBottom: 16 }}
              />
            )}
            {deposit.isLow && !deposit.isExhausted && (
              <Alert
                type="warning"
                showIcon
                message={translate(t, TRANSLATIONS.HOST_DEPOSIT_LOW_WARNING)}
                style={{ borderRadius: 14, marginBottom: 16 }}
              />
            )}

            {renderHeadline()}

            {clientSecret ? (
              <div style={{ marginTop: 20 }}>
                <BookingPaymentStep
                  clientSecret={clientSecret}
                  onPaid={handlePaid}
                  notice={translate(t, TRANSLATIONS.HOST_DEPOSIT_TOP_UP_NOTICE)}
                />
              </div>
            ) : (
              renderCalculator()
            )}

            {renderMovements()}

            {deposit.balance > 0 && !clientSecret && (
              <Button
                block
                onClick={() => setConfirmClose(true)}
                style={{ borderRadius: 999, marginTop: 24, border: `1px solid ${token.colorBorder}` }}
              >
                {translate(t, TRANSLATIONS.HOST_DEPOSIT_CLOSE_BUTTON)}
              </Button>
            )}
          </div>
        )}
      </PageColumn>

      <ConfirmBottomSheet
        open={confirmClose}
        title={translate(t, TRANSLATIONS.HOST_DEPOSIT_CLOSE_BUTTON)}
        description={translate(t, TRANSLATIONS.HOST_DEPOSIT_CLOSE_CONFIRM)}
        confirmText={translate(t, TRANSLATIONS.HOST_DEPOSIT_CLOSE_BUTTON)}
        cancelText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_KEEP)}
        confirmLoading={closing}
        onConfirm={handleClose}
        onClose={() => setConfirmClose(false)}
      />
    </AppLayout>
  );
};

export default HostDepositPage;
